'use no memo';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import type {
	useTitleContentPropertiesForPublishedPageQuery$data,
	useTitleContentPropertiesForPublishedPageQuery$variables,
} from './__generated__/useTitleContentPropertiesForPublishedPageQuery.graphql.ts';
import { defaultPropertyValue, COVERPICTUREWIDTH } from './contentTopperConstants.ts';
import type { PropertyValue } from './contentPropertyHelper.ts';
import { contentPropertyHelper } from './contentPropertyHelper.ts';

const UseTitleContentPropertiesForPublishedPageQuery = graphql`
	query useTitleContentPropertiesForPublishedPageQuery($contentId: ID, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				pageTitlePublished: properties(key: "page-title-property-published") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				pageTitleDraft: properties(key: "page-title-property-draft") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				metadata {
					frontend {
						coverPictureWidth
					}
				}
			}
		}
	}
`;

export const useTitleContentPropertiesForPublishedPage_content: GraphQLTaggedNode = graphql`
	fragment useTitleContentPropertiesForPublishedPage_content on Content {
		id
		pageTitlePublished: properties(key: "page-title-property-published") {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		pageTitleDraft: properties(key: "page-title-property-draft") {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		metadata {
			frontend {
				coverPictureWidth
			}
		}
	}
`;

export const PreloadHomepageIdQuery: GraphQLTaggedNode = graphql`
	query useTitleContentPropertiesForPublishedPagePreloadHomepageIdQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			homepage {
				id
			}
		}
	}
`;

type useTitleContentPropertiesForPublishedPageProps = {
	contentId?: string;
};

export const getTitleContentProperty = (
	data: any,
): {
	titleContentProperties: PropertyValue;
	coverPictureWidth: COVERPICTUREWIDTH;
} => {
	const titlePropertyContent = data?.content?.nodes?.[0]?.pageTitlePublished?.nodes?.find(
		(node: any) => node?.key == 'page-title-property-published',
	);
	const coverPictureWidth = (data?.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth ||
		COVERPICTUREWIDTH.FULL) as COVERPICTUREWIDTH;

	if (titlePropertyContent?.value) {
		try {
			return {
				titleContentProperties: contentPropertyHelper(titlePropertyContent),
				coverPictureWidth,
			};
		} catch {
			return { titleContentProperties: defaultPropertyValue, coverPictureWidth };
		}
	} else {
		return { titleContentProperties: defaultPropertyValue, coverPictureWidth };
	}
};

const QUERY_OPTIONS = (contentId: string | undefined) => ({
	variables: {
		contentId,
		status: ['current', 'archived', 'draft'],
	},
	fetchPolicy: 'cache-first' as const,
});

export const useTitleContentPropertiesForPublishedPage = ({
	contentId,
}: useTitleContentPropertiesForPublishedPageProps): {
	titleContentProperties: PropertyValue;
	coverPictureWidth: COVERPICTUREWIDTH;
} => {
	const options = QUERY_OPTIONS(contentId);

	const { data, error, loading } = useQueryNoFurtherUpdateInterop<
		useTitleContentPropertiesForPublishedPageQuery$data,
		useTitleContentPropertiesForPublishedPageQuery$variables
	>(UseTitleContentPropertiesForPublishedPageQuery, {
		variables: options.variables,
		skip: !contentId,
	});

	if (!loading && data && !error) {
		const { titleContentProperties, coverPictureWidth } = getTitleContentProperty(data);

		return { titleContentProperties, coverPictureWidth };
	}

	return {
		titleContentProperties: defaultPropertyValue,
		coverPictureWidth: COVERPICTUREWIDTH.FULL,
	};
};
